import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useContratos } from '@/hooks/useContratos'
import { useParceiros } from '@/hooks/useParceiros'
import { useSplits } from '@/hooks/useSplits'
import { Search, Loader2, Handshake, Filter, Calendar, User, Car, ShieldCheck, Wallet, CheckCircle2, Clock, XCircle } from 'lucide-react'
import { formatCurrency } from '@/lib/formatters'

export function ContratosParceirosPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [tab, setTab] = useState<'contratos' | 'splits'>('contratos')
    const [parceiroId, setParceiroId] = useState('todos')
    const [status, setStatus] = useState('todos')
    const [search, setSearch] = useState('')

    useEffect(() => { setPageTitle('Gestão de Parceiros') }, [setPageTitle])

    const { data: parceirosRes } = useParceiros()
    const parceiros = parceirosRes?.data || []

    const { data: contratosRes, isLoading: loadingContratos } = useContratos({
        parceiro_id: parceiroId !== 'todos' ? parceiroId : undefined,
        status: status !== 'todos' ? status : undefined,
        search
    })

    const { data: splitsRes, isLoading: loadingSplits } = useSplits({
        parceiro_id: parceiroId !== 'todos' ? parceiroId : undefined,
        status: status !== 'todos' ? status : undefined
    })

    const statusMap: any = {
        'ativo': { label: 'Ativo', color: 'text-success-600 bg-success-50', icon: CheckCircle2 },
        'pendente_adesao': { label: 'Aguardando Adesão', color: 'text-warning-600 bg-warning-50', icon: Clock },
        'cancelado': { label: 'Cancelado', color: 'text-error-600 bg-error-50', icon: XCircle },
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header com Tabs */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Gestão de Parceiros</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Acompanhamento de contratos e comissões (splits)</p>
                </div>
                
                <div className="flex bg-surface-100 p-1 rounded-lg">
                    <button 
                        onClick={() => setTab('contratos')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${tab === 'contratos' ? 'bg-white text-primary-600 shadow-sm' : 'text-surface-600 hover:text-surface-900'}`}
                    >
                        Contratos
                    </button>
                    <button 
                        onClick={() => setTab('splits')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${tab === 'splits' ? 'bg-white text-primary-600 shadow-sm' : 'text-surface-600 hover:text-surface-900'}`}
                    >
                        Comissões (Splits)
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="glass-card p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-1">
                    <label className="text-xs font-semibold text-surface-500 uppercase mb-1.5 block">Parceiro</label>
                    <select 
                        value={parceiroId} 
                        onChange={(e) => setParceiroId(e.target.value)}
                        className="input input-md"
                    >
                        <option value="todos">Todos os Parceiros</option>
                        {parceiros.map(p => (
                            <option key={p.id} value={p.id}>{p.nome}</option>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-1">
                    <label className="text-xs font-semibold text-surface-500 uppercase mb-1.5 block">Situação</label>
                    <select 
                        value={status} 
                        onChange={(e) => setStatus(e.target.value)}
                        className="input input-md"
                    >
                        <option value="todos">Todas as Situações</option>
                        {tab === 'contratos' ? (
                            <>
                                <option value="ativo">Ativo</option>
                                <option value="pendente_adesao">Aguardando Adesão</option>
                                <option value="cancelado">Cancelado</option>
                            </>
                        ) : (
                            <>
                                <option value="pendente">Pendente</option>
                                <option value="pago">Pago</option>
                                <option value="cancelado">Cancelado</option>
                            </>
                        )}
                    </select>
                </div>

                <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-surface-500 uppercase mb-1.5 block">Busca Rápida</label>
                    <div className="relative">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                        <input 
                            type="text" 
                            placeholder="Buscar por placa, CPF ou contrato..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="input input-md pl-10"
                        />
                    </div>
                </div>
            </div>

            {/* Listagem */}
            <div className="glass-card overflow-hidden">
                {tab === 'contratos' ? (
                    loadingContratos ? (
                        <div className="flex justify-center p-12"><Loader2 className="animate-spin text-primary-600" /></div>
                    ) : contratosRes?.data.length === 0 ? (
                        <div className="text-center py-16 text-surface-400">
                            <Handshake size={48} className="mx-auto mb-4 opacity-20" />
                            <p>Nenhum contrato de parceiro encontrado</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-surface-50/50 border-b border-surface-100">
                                        <th className="px-4 py-3 text-xs font-bold text-surface-500 uppercase">Associado / Contrato</th>
                                        <th className="px-4 py-3 text-xs font-bold text-surface-500 uppercase text-center">Início</th>
                                        <th className="px-4 py-3 text-xs font-bold text-surface-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-xs font-bold text-surface-500 uppercase text-right">Valor</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-surface-100 text-sm">
                                    {contratosRes?.data.map(c => {
                                        const st = statusMap[c.status || ''] || { label: c.status, color: 'bg-surface-100' }
                                        return (
                                            <tr key={c.id} className="hover:bg-surface-50/50 transition-colors cursor-pointer" onClick={() => window.location.href = `/admin/contratos/${c.id}`}>
                                                <td className="px-4 py-4">
                                                    <div className="font-semibold text-surface-900">{c.associado_nome}</div>
                                                    <div className="text-xs text-surface-500 flex items-center gap-1.5 mt-0.5">
                                                        <Car size={13} /> {c.veiculo_placa} • {c.veiculo_marca} {c.veiculo_modelo}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 text-center text-surface-600">
                                                    {c.data_inicio ? new Date(c.data_inicio).toLocaleDateString() : '-'}
                                                </td>
                                                <td className="px-4 py-4">
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium gap-1.5 ${st.color}`}>
                                                        {st.icon && <st.icon size={12} />}
                                                        {st.label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-right font-medium text-surface-900">
                                                    {formatCurrency(c.valor_total_mensal || 0)}
                                                    <div className="text-[10px] text-primary-500 font-bold uppercase tracking-wider mt-0.5">/mês</div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : (
                    /* Aba de Splits */
                    loadingSplits ? (
                        <div className="flex justify-center p-12"><Loader2 className="animate-spin text-primary-600" /></div>
                    ) : splitsRes?.data.length === 0 ? (
                        <div className="text-center py-16 text-surface-400">
                            <Wallet size={48} className="mx-auto mb-4 opacity-20" />
                            <p>Nenhuma comissão (split) registrada</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-surface-50/50 border-b border-surface-100">
                                        <th className="px-4 py-3 text-xs font-bold text-surface-500 uppercase">Parceiro / Associado</th>
                                        <th className="px-4 py-3 text-xs font-bold text-surface-500 uppercase text-center">Fatura Venc.</th>
                                        <th className="px-4 py-3 text-xs font-bold text-surface-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-xs font-bold text-surface-500 uppercase text-right">Comissão</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-surface-100 text-sm">
                                    {splitsRes?.data.map(s => (
                                        <tr key={s.id} className="hover:bg-surface-50/50 transition-colors">
                                            <td className="px-4 py-4">
                                                <div className="font-semibold text-surface-900">{s.parceiro?.nome}</div>
                                                <div className="text-xs text-surface-500 flex items-center gap-1.5 mt-0.5">
                                                    <User size={13} /> {s.fatura?.contrato?.associado?.nome}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-center text-surface-600">
                                                {s.fatura?.data_vencimento ? new Date(s.fatura.data_vencimento).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    s.status === 'pago' ? 'text-success-600 bg-success-50' : 
                                                    s.status === 'pendente' ? 'text-warning-600 bg-warning-50' : 
                                                    'text-error-600 bg-error-50'
                                                }`}>
                                                    {s.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-right font-bold text-primary-600">
                                                {formatCurrency(s.valor)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </div>
        </div>
    )
}
